// Phase 6 — /cook route. README §6.7.
//
// Mount: check that anchoredPlan + planToken are set; if not, redirect
// to `/` (locked-in §10.7 gating decision — no exceptions).
// Then choose the initial mode (the store's enterCooking decides):
//   - A declared preCook/prepCook phase → timed Prep mode.
//   - No timed phase but prerequisites prep → derived PrepCook checklist.
//   - Neither → open in LiveCook.
//
// Mobile portrait: render a calm rotation prompt instead of the pane
// content. Detected via `window.matchMedia('(orientation: portrait) and
// (max-width: 768px)')`.

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Wordmark } from '../design/components';
import { theme } from '../design/theme';
import { useAppStore } from '../state/store';
import { PrepPane } from '../cooking/PrepPane';
import { PrepChecklistPane } from '../cooking/PrepChecklistPane';
import { LiveCookPane } from '../cooking/LiveCookPane';
import { CountdownPane } from '../cooking/CountdownPane';
import { useTickController } from '../cooking/useTickController';
import { useOffline } from '../cooking/useOffline';
import { useEffectiveClassified } from '../cooking/useEffectiveClassified';
import { useTaskActivationSounds } from '../cooking/useTaskActivationSounds';
import { useShouldPromptRotation } from '../cooking/useShouldPromptRotation';
import { phaseStartCueIds } from '../cooking/phaseStartCue';
import { installAudioUnlock } from '../cooking/sounds';
import { correctedNow, durationToSeconds } from '../cooking/helpers';
import { liveCookStartShiftMs, rebaseLiveCook } from '../cooking/rebaseLiveCook';
import { showKofiOverlay } from '../kofiOverlay';

// Stable empty schedule so the effective-classification hook keeps a
// steady identity before the anchored plan is wired.
const EMPTY_SCHEDULE: readonly never[] = [];

export default function Cook() {
  const navigate = useNavigate();
  const anchoredPlan = useAppStore((s) => s.anchoredPlan);
  const planToken = useAppStore((s) => s.planToken);
  const recipe = useAppStore((s) => s.recipe);
  const cookingState = useAppStore((s) => s.cookingState);
  const cookStartAt = useAppStore((s) => s.cookStartAt);
  const liveCookStartedAt = useAppStore((s) => s.liveCookStartedAt);
  const classified = useAppStore((s) => s.classified);
  const completedTaskIds = useAppStore((s) => s.completedTaskIds);
  const clockOffset = useAppStore((s) => s.clockOffset);
  const enterCooking = useAppStore((s) => s.enterCooking);
  const transitionToLiveCook = useAppStore((s) => s.transitionToLiveCook);
  const closeSession = useAppStore((s) => s.closeSession);
  const soundEnabled = useAppStore((s) => s.soundEnabled);

  // Gate: missing plan or token → home.
  useEffect(() => {
    if (!anchoredPlan || !planToken || !recipe) {
      navigate('/');
    }
  }, [anchoredPlan, planToken, recipe, navigate]);

  // Initialise cooking state on first mount.
  useEffect(() => {
    if (anchoredPlan && planToken && recipe && cookingState === 'idle') {
      enterCooking();
    }
  }, [anchoredPlan, planToken, recipe, cookingState, enterCooking]);

  // Countdown flip: while the cook is held dormant, watch the corrected clock
  // and begin the cook for real the instant it reaches the planned start. The
  // phase clocks are seeded from `cookStartAt`, so elapsed time runs from the
  // planned start rather than this flip. Runs regardless of orientation so a
  // phone left in portrait still starts on time.
  useEffect(() => {
    if (cookingState !== 'countdown' || cookStartAt == null) return;
    const check = (): void => {
      if (correctedNow(clockOffset) >= cookStartAt) enterCooking(cookStartAt);
    };
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, [cookingState, cookStartAt, clockOffset, enterCooking]);

  // Start the tick controller for the lifetime of the route.
  useTickController();
  const offline = useOffline();

  // The cook is calm and distraction-free, so the floating Ko-fi "Support Us"
  // widget stays hidden during the cook — but once the meal is done we surface
  // it on the "Enjoy your meal, Chef!" screen (issue 3). The cleanup hides it
  // again when leaving the complete screen; Home re-shows it on its own mount.
  useEffect(() => {
    if (cookingState === 'complete') return showKofiOverlay();
  }, [cookingState]);

  // Browsers keep media muted until a user gesture. Prime the cue clips on
  // the first interaction anywhere on the cook screen so the first real
  // activation cue isn't swallowed by the autoplay policy.
  useEffect(() => installAudioUnlock(), []);

  const portrait = useShouldPromptRotation();

  const nominalDurationSeconds = useMemo(
    () => durationToSeconds(anchoredPlan?.phases.liveCook.nominalDuration),
    [anchoredPlan],
  );

  // The schedule the cook surfaces actually use: the immutable anchored
  // schedule with each phase's "Start" cue omitted (the lane-A0 alarm at the
  // phase start carries no work — README §6.7). Display-only; the stored plan
  // is untouched.
  const startCueIds = useMemo(
    () => phaseStartCueIds(anchoredPlan?.schedule ?? EMPTY_SCHEDULE),
    [anchoredPlan],
  );
  // Early-finish re-base (Option A — DECISIONS §H.2). When prep is confirmed
  // complete before its anchored window ends, LiveCook begins at the actual
  // transition instant instead of idling until the originally-anchored
  // liveStart. `liveStartShiftMs` is the unused prep time; 0 when LiveCook
  // starts on time or late (today's overdue behaviour is preserved). The shift
  // slides only the LiveCook entries earlier — a display/classification overlay;
  // the stored plan and wire schedule are untouched.
  const liveStartShiftMs = useMemo(
    () =>
      liveCookStartShiftMs(anchoredPlan?.phases.liveCook.startAt, liveCookStartedAt, clockOffset),
    [anchoredPlan, liveCookStartedAt, clockOffset],
  );

  const schedule = useMemo(
    () =>
      rebaseLiveCook(
        (anchoredPlan?.schedule ?? EMPTY_SCHEDULE).filter((t) => !startCueIds.has(t.id)),
        liveStartShiftMs,
      ),
    [anchoredPlan, startCueIds, liveStartShiftMs],
  );

  // The plan the panes actually render: server classification when it has
  // answered, a local wall-clock fallback otherwise, always with optimistic
  // completion overlaid. See useEffectiveClassified. When LiveCook has been
  // re-based (early finish), the server still anchors to the original liveStart
  // and can't know about the client-driven transition, so we classify the
  // re-based schedule locally (README §9/§10.2 sanction running the cook off the
  // cached schedule); otherwise the server stays authoritative.
  const effectiveClassified = useEffectiveClassified(
    schedule,
    liveStartShiftMs > 0 ? null : classified,
    completedTaskIds,
    clockOffset,
  );

  // Drop any Start-cue classification the server still sends, so it never
  // renders, enters the outstanding panel, or sounds an activation cue.
  const visibleClassified = useMemo(
    () => ({
      ...effectiveClassified,
      tasks: effectiveClassified.tasks.filter((ct) => !startCueIds.has(ct.task.id)),
    }),
    [effectiveClassified, startCueIds],
  );

  // Play a short cue (whistle / bell / drum, keyed off task.kind) whenever a
  // task enters its active window. Centralised here so it fires once per
  // session across all three cooking modes, regardless of which pane (or how
  // many panes) is mounted. Gated on the speaker chip's state.
  useTaskActivationSounds(visibleClassified, soundEnabled);

  if (!anchoredPlan || !planToken || !recipe) {
    return null;
  }

  // The end-of-cook "Enjoy your meal!" screen shows in any orientation — it's
  // a terminal message, not the landscape-locked cook — and sits mid-screen,
  // sized for mobile. Rendered before the rotation gate so a phone held
  // upright still gets it.
  if (cookingState === 'complete') {
    return (
      <main
        className="cc-cook-pane"
        key="complete"
        aria-label="Cook complete"
        style={{
          minHeight: '100dvh',
          padding: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          background: theme.bg.base,
          color: theme.text.primary,
        }}
      >
        <Wordmark size="sm" as="h1" />
        <p
          style={{
            margin: 0,
            fontFamily: '"Caveat", cursive',
            fontStyle: 'italic',
            fontWeight: 600,
            fontSize: 'clamp(2.25rem, 9vw, 3.5rem)',
            lineHeight: 1.05,
            color: theme.accent.action,
          }}
        >
          Enjoy your meal
        </p>
        <p
          style={{
            margin: 0,
            fontFamily: '"Caveat", cursive',
            fontStyle: 'italic',
            fontWeight: 700,
            fontSize: 'clamp(3.25rem, 14vw, 5.5rem)',
            lineHeight: 1.05,
            color: theme.accent.action,
          }}
        >
          Chef!
        </p>
        <Button
          variant="primary"
          onClick={() => {
            closeSession();
            navigate('/');
          }}
        >
          Close
        </Button>
      </main>
    );
  }

  if (portrait) {
    return (
      <main
        aria-label="Rotation prompt"
        style={{
          padding: '2rem 1rem',
          background: theme.bg.base,
          color: theme.text.primary,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Wordmark size="md" as="h1" />
        <p style={{ maxWidth: '20rem', fontSize: '1.1rem' }}>
          Please rotate your phone to landscape.
        </p>
        <p style={{ maxWidth: '20rem', color: theme.text.muted }}>
          The cooking screen needs the wider view.
        </p>
      </main>
    );
  }

  // Dormant Countdown screen — read-only read-ahead until the start instant
  // arrives (the flip effect above swaps cookingState to the first live phase).
  if (cookingState === 'countdown') {
    return (
      <div className="cc-cook-pane" key="countdown">
        <CountdownPane />
      </div>
    );
  }

  const handleTransition = (prepElapsedSecs: number): void => {
    transitionToLiveCook(prepElapsedSecs, Date.now());
  };

  return (
    <div
      style={{
        background: theme.bg.base,
        color: theme.text.primary,
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {offline && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            padding: '0.4rem 1rem',
            background: theme.bg.raised,
            color: theme.text.primary,
            fontSize: '0.85rem',
          }}
        >
          Working offline — I&apos;ll catch up when the connection&apos;s back.
        </div>
      )}

      {cookingState === 'prep' && (
        <div className="cc-cook-pane" key="prep">
          <PrepPane
            schedule={schedule}
            classified={visibleClassified}
            onTransition={handleTransition}
          />
        </div>
      )}
      {cookingState === 'prepChecklist' && (
        <div className="cc-cook-pane" key="prepChecklist">
          <PrepChecklistPane onTransition={handleTransition} />
        </div>
      )}
      {cookingState === 'liveCook' && (
        <div className="cc-cook-pane" key="liveCook">
          <LiveCookPane
            schedule={schedule}
            classified={visibleClassified}
            nominalDurationSeconds={nominalDurationSeconds}
            recipe={recipe}
          />
        </div>
      )}
    </div>
  );
}
